import { IframeWidget } from '.';
import { Log } from '../../AuxillaryService/Log';
import { platform, supportsExecuteJavascriptOnWindow, supportsMultiWindow } from '../../Platform';
import { asyncTimeout } from '../../Utility/AsyncTimeout';
import { JsonValue } from '../../Utility/JsonValue';
import { BaseIframeWidget } from './types';

/**
 * Windowed Iframe
 * @class
 * @augments VirtualIframe
 * @param {Widget} widget
 */
export class WindowedIframe implements BaseIframeWidget {
    /**
     * the handle to the iframe UI window
     */
    handle = -1;

    /**
     * Iframe widget object
     */
    widget: IframeWidget;

    constructor(widget: IframeWidget) {
        this.widget = widget;
    }

    /**
     * Draws the iframe window onto the UI
     */
    async render(): Promise<void> {
        const thePlatform = platform();
        if (!supportsMultiWindow(thePlatform)) {
            Log.Warning(
                'The current platform does not support Windowed Iframe',
                null,
                __fileline,
                null
            );
            return;
        }

        const bounds = this.widget.wrapperElement.getBoundingClientRect();
        this.handle = await thePlatform.createWindow({
            rectangle: {
                height: bounds.height,
                width: bounds.width,
                left: bounds.left,
                top: bounds.top,
            },
            url: this.widget.config.url,
        });
    }

    /**
     * removes the iframe window from the UI
     */
    async destroy(): Promise<void> {
        const thePlatform = platform();

        if (!supportsMultiWindow(thePlatform)) {
            Log.Warning(
                'The current platform does not support Windowed Iframe',
                null,
                __fileline,
                null
            );
            return;
        }

        try {
            await thePlatform.destroyWindow(this.handle);
        } catch (e) {
            Log.Error('Failed destroying the window', null, __fileline, e);
        } finally {
            this.handle = -1;
        }
    }

    /**
     * executes the given JS on the windowed Iframe
     * @returns JSON value on success or an string starting with '[ERROR]' on failure
     */
    async executeJS(jsToExec: string): Promise<JsonValue> {
        const thePlatform = platform();
        if (!supportsExecuteJavascriptOnWindow(thePlatform)) {
            Log.Warning("Can't execute Javascript on the current platform", null, __fileline, null);
            return "[ERROR] Can't execute Javascript on the current platform";
        }

        let result;
        try {
            result = await thePlatform.executeJsOnWindow(this.handle, jsToExec);
        } catch (err) {
            Log.Warning(
                `Failed to execute JavaScript on handle #${this.handle}: ${String(err)}`,
                null,
                __fileline,
                err
            );
            return `[ERROR] ${String(err)}`;
        }

        return result;
    }

    /**
     * Attempt to load iframe. UI display handled by virtual iframe
     */
    async attemptIframeLoad(): Promise<void> {
        if (!this.widget.loginScriptStarted) {
            this.widget.loginScriptStarted = true;
            // ignored promise.
            void this.widget.startLoginScript();
        }

        const thePlatform = platform();

        /**
         * if we can't execute javascript `refreshWindowedIframe` will run forever.
         */
        if (supportsExecuteJavascriptOnWindow(thePlatform)) {
            /**
             * Tell the browser to refresh.
             * ignored promise.
             */
            void this.refreshWindowedIframe();
        }
    }

    /**
     * Creates a setTimeout to wait for the iframe reload to run, then refreshes
     *   the styling after the new iframe body and scripts are loaded.
     */
    async refreshWindowedIframe(): Promise<void> {
        void this.executeJS('location.reload()');

        // wait for the reload to run
        await asyncTimeout(1000);
        // worker to watch for when the iframe has loaded
        const waitUntilLoaded = setInterval(async () => {
            const bodyLoaded =
                (await this.executeJS('document.querySelector("body") !== null')) === true;

            if (bodyLoaded) {
                clearInterval(waitUntilLoaded);

                // wait until any JS on the page has run
                await asyncTimeout(1000);
                // apply the CSS for scroll, scaling, and custom styling/CSS
                let additionalStyling = ';';
                const css = this.widget.getIframeCSS();

                Object.entries(css).forEach(([key, value]) => {
                    if (key !== 'width' && key !== 'height') {
                        additionalStyling += `${key}:${value};`;
                    }
                });

                void this.executeJS(
                    'document.body.insertBefore(document.createElement("style"),' +
                        ' document.body.firstChild).innerHTML = "html { ' +
                        (this.widget.config.css ?? '') +
                        additionalStyling +
                        ' }"'
                );
            }
        }, 100);
    }
}
